// SPDX-License-Identifier: AGPL-3.0-or-later
import {LRUCache} from 'lru-cache';

interface ImageCacheEntry {
	image: HTMLImageElement;
}
interface PendingImageLoad {
	image: HTMLImageElement;
	onLoadCallbacks: Set<() => void>;
	onErrorCallbacks: Set<() => void>;
}
const imageCache = new LRUCache<string, ImageCacheEntry>({
	max: 500,
	dispose: (entry) => {
		entry.image.onload = null;
		entry.image.onerror = null;
		entry.image.src = '';
	},
});
const pendingImageLoads = new Map<string, PendingImageLoad>();
const isCached = (src: string | null): boolean => {
	if (!src) return false;
	return imageCache.has(src);
};
export function hasImage(src: string | null): boolean {
	return isCached(src);
}
export function loadImage(src: string | null, onLoad: () => void, onError?: () => void): void {
	if (!src) {
		onError?.();
		return;
	}
	if (imageCache.has(src)) {
		imageCache.get(src);
		onLoad();
		return;
	}
	const pendingLoad = pendingImageLoads.get(src);
	if (pendingLoad) {
		pendingLoad.onLoadCallbacks.add(onLoad);
		if (onError) {
			pendingLoad.onErrorCallbacks.add(onError);
		}
		return;
	}
	const image = new Image();
	const onLoadCallbacks = new Set<() => void>([onLoad]);
	const onErrorCallbacks = new Set<() => void>();
	if (onError) {
		onErrorCallbacks.add(onError);
	}
	pendingImageLoads.set(src, {image, onLoadCallbacks, onErrorCallbacks});
	image.onload = () => {
		imageCache.set(src, {image});
		const currentPendingLoad = pendingImageLoads.get(src);
		pendingImageLoads.delete(src);
		if (!currentPendingLoad) return;
		for (const callback of currentPendingLoad.onLoadCallbacks) {
			callback();
		}
	};
	image.onerror = () => {
		const currentPendingLoad = pendingImageLoads.get(src);
		pendingImageLoads.delete(src);
		if (!currentPendingLoad) return;
		for (const callback of currentPendingLoad.onErrorCallbacks) {
			callback();
		}
	};
	image.src = src;
}
export function _clearForTests(): void {
	imageCache.clear();
	pendingImageLoads.clear();
}
